/**
 * Builds a flat, ground-lying decal quad (see types.ts's `Decal`). Shared
 * between the live game (always visible — real ground detail, not a debug
 * aid) and `ultra_map_editor` (visible + selectable there), so both render
 * from the exact same function.
 *
 * Alpha-cutout, per the request that spawned this: `alphaTest` discards
 * near-fully-transparent background pixels outright (avoids the
 * "semi-transparent quad tucked wrong in the depth sort" artifact a flat
 * ground decal would otherwise show), while `transparent: true` still lets
 * genuinely semi-transparent edge pixels (an anti-aliased or soft-glow
 * silhouette) blend smoothly rather than being clipped to a hard edge.
 *
 * No caching/disposal-safety dance like `model-attach.ts`'s GLB loader — a
 * decal texture is a plain, cheap-to-decode image with no shared-geometry
 * hazard, so each mesh just loads (and later disposes) its own texture.
 */
import * as THREE from 'three';
import type { Decal } from '../types';

const textureLoader = new THREE.TextureLoader();

export function buildDecalMesh(decal: Decal): THREE.Object3D {
  const texture = textureLoader.load(decal.textureUrl);
  texture.colorSpace = THREE.SRGBColorSpace;

  const material = new THREE.MeshBasicMaterial({
    map: texture,
    transparent: true,
    alphaTest: decal.hardness ?? 0.1,
    depthWrite: false,
    side: THREE.DoubleSide,
    opacity: decal.opacity ?? 1,
  });

  // Unit-sized geometry, sized via the group's `.scale` instead — so
  // ultra_map_editor's TransformControls scale mode can drive width/depth
  // directly (`group.scale.x`/`.z`) without rebuilding PlaneGeometry on
  // every drag frame. Same technique as debug-zones.ts's `buildZoneMesh`.
  // Composition order (THREE always applies T * R * S) makes this correct
  // even with the child's own rotation and the group's rotationY: scale
  // stretches the flat shape along its own unrotated axes first (defining
  // its w x d before anything spins), rotationY then spins that sized
  // rectangle rigidly, so a rotated decal doesn't shear.
  const plane = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), material);
  plane.rotation.x = -Math.PI / 2; // lie flat, facing +Y

  // Wrapped in a group so `rotationY` is an unambiguous world-Y yaw,
  // independent of the plane's own already-applied "lie flat" rotation —
  // composing a second rotation onto an already-rotated mesh's local axes
  // would not mean what `rotationY`'s yaw convention means everywhere else.
  const group = new THREE.Group();
  group.add(plane);
  group.scale.set(decal.size.w, 1, decal.size.d);
  group.position.set(decal.position.x, 0.01, decal.position.z); // tiny lift — avoids z-fighting with the ground plane
  group.rotation.y = decal.rotationY ?? 0;
  return group;
}
